import * as React from 'react';
import { Outlet } from 'react-router-dom';

import FocusTrap from 'focus-trap-react';
import { LazyMotion, m as motion, useCycle } from 'framer-motion';
import styled, { keyframes } from 'styled-components';

import { ColorContext, DEFAULT_COLOR } from '../Context';
import { media } from '../GlobalStyle/media';
import Spinner from '../Splashes/Spinner';

import Logo from './logo';

// Make sure to return the specific export containing the feature bundle.
const loadFeatures = () =>
  import('./framerMotionFeatures').then((res) => res.default);

const About = React.lazy(() => import('../About'));
const Portfolio = React.lazy(() => import('../Portfolio'));
const MotionToggle = React.lazy(() => import('../MotionToggle'));

const backgroundVariants = {
  open: {
    clipPath: 'inset(0)'
  },
  closed: {
    clipPath: 'inset(0 100% 0 0)'
  }
};

const BackgroundContain = styled(motion.div)`
  position: absolute;
  z-index: 2;
  display: flex;
  overflow: hidden;
  flex-direction: row;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  background: 'transparent';
`;

const variants = {
  open: {
    transition: { staggerChildren: 0.025, delayChildren: 0.1 }
  },
  closed: {
    transition: { staggerChildren: 0.025, staggerDirection: -1 }
  }
};

const backgroundSlots = 20;

const BackgroundPanel = styled(motion.div)`
  position: relative;
  width: calc(100% / ${backgroundSlots} + 4px);
  height: 100%;
  position: relative;
  margin: 0 -2px;
  background: #333;
`;

const backgroundArr = Array(backgroundSlots).fill(1);

const contentVariants = {
  open: {
    transition: {
      delayChildren: 1,
      staggerChildren: 0.3
    }
  },
  closed: {}
};

const sectionVariants = {
  open: {
    y: 0,
    opacity: 1,
    transition: {
      y: { stiffness: 1000, velocity: -100 }
    }
  },
  closed: {
    y: 50,
    opacity: 0,
    transition: {
      y: { stiffness: 1000 }
    }
  }
};

const buttonVariants = {
  open: {
    opacity: 1
  },
  closed: {
    opacity: 0
  }
};

const ContentContain = styled(motion.div)`
  position: absolute;
  display: grid;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 2;
  grid-template-columns: 100%;

  ${media.small`
	/* like the app store. */
	grid-template-columns: 70% 30%;
	`}
  ${media.medium`
	grid-template-columns: repeat(2, 50%);
	`}
	
	${media.mediumLarge`
		grid-template-columns: 41% 59%;
	`}

  ${media.xlarge`
		grid-template-columns: repeat(3, 33%);
	`}
`;

const CloseButton = styled(motion.button)`
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 3;
  border-radius: 50%;
  padding: 4px;
  width: 30px;
  height: 30px;
  box-sizing: content-box;
  text-align: center;
  display: flex;
  align-items: center;
  background-color: rgb(46, 46, 46);
  font-size: 1.75rem;
  border: 1px solid transparent;

  &:hover,
  &:focus {
    border: 1px solid #fff;
  }

  svg {
    width: 100%;
  }
`;

interface INav {
  $interact: boolean;
}
const Navigation = styled(motion.div)<INav>`
  /* content-visibility: ${({ $interact }) =>
    $interact ? 'visible' : 'hidden'}; */
  pointer-events: ${({ $interact }) => ($interact ? 'unset' : 'none')};
`;

const Nav = styled.div`
  position: fixed;
  display: flex;
  flex-direction: row;
  top: 10px;
  left: 15px;
  overflow: hidden;
  justify-content: space-between;
  align-items: baseline;
  width: 150px;
  z-index: 2;
  ${media.small`
		width: 120px;
    top: 30px;
    left: 30px;
	`}
`;

const Contain = styled.div<INav>`
  /* content-visibility: ${({ $interact }) =>
    $interact ? 'visible' : 'hidden'}; */
  pointer-events: ${({ $interact }) => ($interact ? 'unset' : 'none')};
`;

const Section = styled(motion.div)`
  width: 100%;
  height: 100%;
  padding: 30px;
  color: #fff;
  display: flex;
  flex-direction: column;
  overflow-y: scroll;
`;

const hoverMovement = keyframes`
	0% {
		color: rgb(216, 254, 255);
	}
	100% {
		color: rgb(75, 214, 214);
	}
`;

interface ILogo {
  $color: string | undefined;
}

const LogoButon = styled.button<ILogo>`
  width: 75px;
  font-size: 0.75em;
  color: ${({ $color = DEFAULT_COLOR }) => $color};
  transition: color 0.3s ease;

  &:hover,
  &:focus {
    transition: color 0.3s ease;
    animation: none;
    /* TODO: some brighter color..? */
    color: ${({ $color = DEFAULT_COLOR }) => $color};
  }

  svg {
    width: 100%;
  }

  ${media.small`
    width: 100%;
    font-size: 1rem;
  `}
`;

const Background: React.FC = () => (
  <BackgroundContain variants={variants}>
    {backgroundArr.map((_val, index) => (
      <BackgroundPanel
        variants={backgroundVariants}
        transition={{ duration: 0.3, type: 'tween' }}
        key={index}
      />
    ))}
  </BackgroundContain>
);

const Content = () => (
  <ContentContain variants={contentVariants}>
    <Section variants={sectionVariants}>
      <React.Suspense fallback={<Spinner />}>
        <About />
      </React.Suspense>
    </Section>
    {/* <Section variants={sectionVariants}>
      <LeftInfo />
    </Section>
    <Section variants={sectionVariants}>
      <RightInfo />
    </Section> */}
  </ContentContain>
);

const ButtonText = styled.div`
  position: relative;
  top: -10px;
  left: 25px;
  letter-spacing: 2px;
`;

const Page: React.FC = () => {
  const [color, setColor] = React.useState<string>(DEFAULT_COLOR);
  const [isOpen, toggleOpen] = useCycle(false, true);
  // TODO: this should use the react transition api
  const handleToggle = React.useCallback(() => toggleOpen(), [toggleOpen]);

  const handleColor = React.useCallback((value: string) => setColor(value), []);

  return (
    <ColorContext.Provider value={[color, handleColor]}>
      <LazyMotion strict={true} features={loadFeatures}>
        <FocusTrap
          active={!isOpen}
          focusTrapOptions={{
            escapeDeactivates: false
          }}
        >
          <Contain $interact={!isOpen}>
            <Nav>
              <LogoButon
                onClick={handleToggle}
                disabled={isOpen}
                $color={color}
              >
                <Logo />
                <ButtonText>Menu</ButtonText>
              </LogoButon>
            </Nav>
            <React.Suspense fallback={<Spinner />}>
              <Portfolio isOpen={isOpen} />
            </React.Suspense>
          </Contain>
        </FocusTrap>
        <Navigation
          $interact={isOpen}
          initial={false}
          animate={isOpen ? 'open' : 'closed'}
        >
          <FocusTrap
            active={isOpen}
            focusTrapOptions={{
              escapeDeactivates: false
            }}
          >
            <div>
              <Background />
              <Content />
              <CloseButton onClick={handleToggle} variants={buttonVariants}>
                <svg focusable="false" viewBox="0 0 24 24" fill="#fff">
                  <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                </svg>
              </CloseButton>
            </div>
          </FocusTrap>
        </Navigation>
        <React.Suspense fallback={null}>
          <MotionToggle />
        </React.Suspense>
        <Outlet />
      </LazyMotion>
    </ColorContext.Provider>
  );
};

export default Page;
